/**
 * 채용 목록 조건 — 주소(쿼리) ↔ 화면 상태 ↔ 서버 파라미터 (세미나 seminarListQuery.ts 와 같은 모양).
 *
 * 주소:
 * - `category` REGION|SUBWAY — 지역별 / 지하철역별 탭(홈 바로가기가 각각으로 보낸다. 옛 `tab=region|subway` 도 읽는다)
 * - `districts` `시도id_구id,…` (`시도id_0` = 시·도 전체, 없거나 `0_0` = 전국 — 목록 첫 화면 기본값은 전국, 2026-09-29 사용자 결정)
 * - `subways` 역 id,… (옛 링크의 `노선id_0`(노선 전체)·`AREA_n`(지역 전체)도 그대로 서버에 넘긴다)
 * - `jobTasks` · `recruitmentTypes` · `career` · `workdays`(근무 요일) · `workPeriod`(근무 기간) · `workDates`(근무 날짜 yyyy-MM-dd,…) · `welfare` · `emergency=true` · `payTiming`(급여 지급 시점, SAME_DAY = 당일 지급) · `search`
 * 서버(`GET /recruitments/region` · `/recruitments/station`): `regionIds` | `stationIds`·`areaIds`, `jobTaskTypes` · `recruitmentTypes` ·
 *   `careerTypes` · `workdays` · `workPeriod` · `workDates` · `welfareTypes` · `emergency` · `payTiming`. 복지·근무 요일·기간·날짜·지급 시점은 서버가 거른다(페이지·전체 수가 맞게).
 * 조건은 모두 주소에 두어 새로고침·뒤로가기(목록 복원)로 그대로 돌아온다.
 */

export type RecruitmentTab = 'region' | 'subway';

/** 목록 정렬 — 서버 `sort` 값과 같다(core RecruitmentListSort). 어떤 정렬이든 모집 중인 공고가 먼저다 */
export type RecruitmentSort = 'recommended' | 'latest' | 'deadline' | 'payHigh' | 'workStart' | 'views';

export const RECRUITMENT_SORT_DEFAULT: RecruitmentSort = 'recommended';

export const RECRUITMENT_SORT_OPTIONS: { name: string; value: RecruitmentSort }[] = [
    { name: '추천순', value: 'recommended' },
    { name: '최신 등록순', value: 'latest' },
    { name: '마감 임박순', value: 'deadline' },
    { name: '급여 높은순', value: 'payHigh' },
    { name: '근무 시작 빠른순', value: 'workStart' },
    { name: '조회 많은순', value: 'views' },
];

/** 주소의 정렬 값 — 모르는 값은 추천순 */
export const parseRecruitmentSort = (raw: string | null): RecruitmentSort =>
    RECRUITMENT_SORT_OPTIONS.find((option) => option.value === raw)?.value ?? RECRUITMENT_SORT_DEFAULT;

export interface RecruitmentListQuery {
    tab: RecruitmentTab;
    /** 고른 지역 키(`시도id_구id`). 비어 있으면 전국 */
    districts: string[];
    /** 고른 역 id. 비어 있으면(지하철 탭) 전국 목록 */
    stations: string[];
    jobTasks: string[];
    recruitmentTypes: string[];
    careers: string[];
    /** 근무 요일(월~금만·토요일 격주·토요일 근무·주 4일 이하·요일 협의) */
    workdays: string[];
    /** 근무 기간(계속·기간·특정 날짜) */
    periods: string[];
    /**
     * 근무 날짜(`yyyy-MM-dd`) — 고른 날 중 하루라도 겹치는 **기간 근무·특정 날짜 공고만** 나온다(기간 제한 없는 공고는 빠진다).
     * 단기 공고를 날짜로 찾는 조건(2026-10-01 사용자 결정).
     */
    workDates: string[];
    welfares: string[];
    emergency: boolean;
    /** 급여 지급 시점(서버 PayTiming 코드) — 지금은 당일 지급(SAME_DAY)만 고를 수 있다. 여러 개면 하나라도 맞는 공고 */
    payTimings: string[];
    /** 정렬 — 거르는 조건이 아니다(조건 개수·초기화에 들어가지 않는다). 검색어가 있을 때는 쓰지 않는다 */
    sort: RecruitmentSort;
    /** 헤더 검색에서 넘어온 검색어 — 다른 조건과 함께 쓰지 못한다(검색 API 가 따로라서, 조건을 바꾸면 검색어는 풀린다) */
    search: string;
}

export interface RecruitmentOption {
    name: string;
    value: string;
}

export const NATIONWIDE_KEY: string = '0_0';
/** 지하철 "지역 전체"(옛 링크) 값 접두어 — 서버에는 areaIds 로 */
export const AREA_ALL_PREFIX: string = 'AREA_';

export const RECRUITMENT_TABS: { key: RecruitmentTab; label: string }[] = [
    { key: 'region', label: '지역별' },
    { key: 'subway', label: '지하철역별' },
];

/** 근무 요일(서버 WorkdaysFilter 코드와 같다, 2026-09-30 사용자 확정 — 요일 중심) */
export const WORKDAY_OPTIONS: RecruitmentOption[] = [
    { name: '월~금만', value: 'WEEKDAYS_ONLY' },
    { name: '토요일 격주', value: 'SAT_BIWEEKLY' },
    { name: '토요일 근무', value: 'SAT_WORK' },
    { name: '주 4일 이하', value: 'FOUR_OR_LESS' },
    { name: '요일 협의', value: 'NEGOTIABLE' },
];

/** 급여 지급 시점(서버 PayTiming 코드와 같다, 2026-10-05) — 근무 후 N일 내·월 1회가 붙으면 여기에 더한다 */
export const PAY_TIMING_FILTER_OPTIONS: RecruitmentOption[] = [
    { name: '당일 지급', value: 'SAME_DAY' },
];

/** 당일 지급 코드 — 칩 줄(PC)·조작 줄(폰)의 바로 켜고 끄는 [당일 지급] */
export const SAME_DAY_PAY_TIMING: string = 'SAME_DAY';

/** [당일 지급] 켜고 끄기 — 다른 지급 시점(앞으로 더할 것)은 그대로 둔다 */
export const toggleSameDayPay = (query: RecruitmentListQuery): RecruitmentListQuery => ({
    ...query,
    payTimings: query.payTimings.includes(SAME_DAY_PAY_TIMING)
        ? query.payTimings.filter((value: string) => value !== SAME_DAY_PAY_TIMING)
        : [...query.payTimings, SAME_DAY_PAY_TIMING],
});

/** 근무 기간(서버 WorkPeriodType 코드와 같다) */
export const WORK_PERIOD_OPTIONS: RecruitmentOption[] = [
    { name: '기간 제한 없음', value: 'ONGOING' },
    { name: '기간 근무', value: 'PERIOD' },
    { name: '특정 날짜', value: 'DATES' },
];

/** 옛 근무요일(`workingDays=FIVE_DAYS`) 링크 → 근무 요일 */
const LEGACY_WORKING_DAYS: Record<string, string> = {
    FIVE_DAYS: 'WEEKDAYS_ONLY',
    FIVE_DAYS_40_HOURS: 'WEEKDAYS_ONLY',
    SIX_DAYS_DAY_OFF: 'SAT_BIWEEKLY',
    SIX_DAYS: 'SAT_WORK',
};

const DISTRICT_KEY_PATTERN: RegExp = /^\d+_\d+$/;
const WORK_DATE_PATTERN: RegExp = /^\d{4}-\d{2}-\d{2}$/;
/** 한 번에 고를 수 있는 근무 날짜 수(서버 RecruitmentListFilter.MAX_WORK_DATES 와 같다) */
export const MAX_WORK_DATES: number = 31;
const ONGOING_PERIOD: string = 'ONGOING';

/** 근무 날짜 정리 — 형식이 맞는 것만, 중복 없이 날짜순, 최대 MAX_WORK_DATES 개 */
export const normalizeWorkDates = (values: string[]): string[] =>
    Array.from(new Set(values.filter((value: string) => WORK_DATE_PATTERN.test(value)))).sort().slice(0, MAX_WORK_DATES);

/** `2026-10-14` → `10/14` (칩 표시) */
export const workDateName = (date: string): string => `${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))}`;

/**
 * 근무 날짜를 바꾼다. 날짜 조건은 기간 근무·특정 날짜 공고에만 걸리므로, 기간이 "기간 제한 없음"만 골라져 있으면
 * 결과가 항상 0건이라 그 선택을 푼다.
 */
export const withWorkDates = (query: RecruitmentListQuery, dates: string[]): RecruitmentListQuery => {
    const workDates: string[] = normalizeWorkDates(dates);
    const onlyOngoing: boolean = query.periods.length === 1 && query.periods[0] === ONGOING_PERIOD;
    return { ...query, workDates, periods: workDates.length > 0 && onlyOngoing ? [] : query.periods };
};

/** 근무 기간을 바꾼다. "기간 제한 없음"만 남으면 근무 날짜 조건은 푼다(같이 쓰면 항상 0건) */
export const withWorkPeriods = (query: RecruitmentListQuery, periods: string[]): RecruitmentListQuery => {
    const onlyOngoing: boolean = periods.length === 1 && periods[0] === ONGOING_PERIOD;
    return { ...query, periods, workDates: onlyOngoing ? [] : query.workDates };
};

const splitParam = (value: string | null): string[] =>
    (value ?? '').split(',').map((item: string) => item.trim()).filter(Boolean);

const unique = (values: string[]): string[] => Array.from(new Set(values));

const onlyKnown = (values: string[], options: RecruitmentOption[]): string[] =>
    unique(values).filter((value: string) => options.some((option: RecruitmentOption) => option.value === value));

export const parseRecruitmentListQuery = (params: URLSearchParams): RecruitmentListQuery => {
    const category: string = (params.get('category') ?? '').toUpperCase();
    const legacyTab: string | null = params.get('tab');
    const tab: RecruitmentTab = category === 'SUBWAY' || (category === '' && legacyTab === 'subway') ? 'subway' : 'region';
    const legacyDays: string[] = splitParam(params.get('workingDays'))
        .map((value: string) => LEGACY_WORKING_DAYS[value])
        .filter((value: string | undefined): value is string => Boolean(value));
    return {
        tab,
        // 전국(0_0)·잘못된 값(`districts=0` 같은 옛 링크)은 "고른 지역 없음" = 전국
        districts: unique(splitParam(params.get('districts'))).filter((key: string) => DISTRICT_KEY_PATTERN.test(key) && key !== NATIONWIDE_KEY),
        stations: unique(splitParam(params.get('subways'))),
        jobTasks: unique(splitParam(params.get('jobTasks'))),
        recruitmentTypes: unique(splitParam(params.get('recruitmentTypes'))),
        careers: unique(splitParam(params.get('career'))),
        workdays: onlyKnown([...splitParam(params.get('workdays')), ...legacyDays], WORKDAY_OPTIONS),
        periods: onlyKnown(splitParam(params.get('workPeriod')), WORK_PERIOD_OPTIONS),
        workDates: normalizeWorkDates(splitParam(params.get('workDates'))),
        welfares: unique(splitParam(params.get('welfare'))),
        emergency: params.get('emergency') === 'true',
        payTimings: onlyKnown(splitParam(params.get('payTiming')), PAY_TIMING_FILTER_OPTIONS),
        sort: parseRecruitmentSort(params.get('sort')),
        search: params.get('search') ?? '',
    };
};

/** 조건을 주소 쿼리에 쓴다 — 목록 조건이 아닌 다른 파라미터는 그대로 두고, 옛 키(tab·subwayArea·subwayLines·workingDays)는 치운다 */
export const writeRecruitmentListQuery = (base: URLSearchParams, query: RecruitmentListQuery): URLSearchParams => {
    const params: URLSearchParams = new URLSearchParams(base);
    const setOrDelete = (key: string, value: string): void => {
        if (value) params.set(key, value);
        else params.delete(key);
    };
    params.set('category', query.tab === 'subway' ? 'SUBWAY' : 'REGION');
    setOrDelete('districts', query.districts.length > 0 ? query.districts.join(',') : NATIONWIDE_KEY);
    setOrDelete('subways', query.stations.join(','));
    setOrDelete('jobTasks', query.jobTasks.join(','));
    setOrDelete('recruitmentTypes', query.recruitmentTypes.join(','));
    setOrDelete('career', query.careers.join(','));
    setOrDelete('workdays', query.workdays.join(','));
    setOrDelete('workPeriod', query.periods.join(','));
    setOrDelete('workDates', query.workDates.join(','));
    setOrDelete('welfare', query.welfares.join(','));
    setOrDelete('emergency', query.emergency ? 'true' : '');
    setOrDelete('payTiming', query.payTimings.join(','));
    setOrDelete('sort', query.sort === RECRUITMENT_SORT_DEFAULT ? '' : query.sort);
    setOrDelete('search', query.search);
    ['tab', 'subwayArea', 'subwayLines', 'workingDays'].forEach((key: string) => params.delete(key));
    return params;
};

export interface RecruitmentApiRequest {
    kind: 'region' | 'station';
    params: Record<string, string | string[]>;
}

/**
 * 서버 목록 요청(api.recruitments.listPage). 지역 탭은 고른 지역(없으면 전국 0_0),
 * 지하철 탭은 고른 역 — 역을 안 골랐으면 전국 목록(예전 "전국 전체"와 같다).
 */
export const toRecruitmentApiRequest = (query: RecruitmentListQuery): RecruitmentApiRequest => {
    const params: Record<string, string | string[]> = {};
    if (query.jobTasks.length > 0) params.jobTaskTypes = query.jobTasks;
    if (query.recruitmentTypes.length > 0) params.recruitmentTypes = query.recruitmentTypes;
    if (query.careers.length > 0) params.careerTypes = query.careers;
    if (query.workdays.length > 0) params.workdays = query.workdays;
    if (query.periods.length > 0) params.workPeriod = query.periods;
    if (query.workDates.length > 0) params.workDates = query.workDates;
    if (query.welfares.length > 0) params.welfareTypes = query.welfares;
    if (query.emergency) params.emergency = 'true';
    if (query.payTimings.length > 0) params.payTiming = query.payTimings;
    if (query.sort !== RECRUITMENT_SORT_DEFAULT) params.sort = query.sort;

    if (query.tab === 'subway' && query.stations.length > 0) {
        const areaIds: string[] = query.stations.filter((id: string) => id.startsWith(AREA_ALL_PREFIX)).map((id: string) => id.slice(AREA_ALL_PREFIX.length));
        const stationIds: string[] = query.stations.filter((id: string) => !id.startsWith(AREA_ALL_PREFIX));
        if (stationIds.length > 0) params.stationIds = stationIds;
        if (areaIds.length > 0) params.areaIds = areaIds;
        return { kind: 'station', params };
    }
    params.regionIds = query.tab === 'region' && query.districts.length > 0 ? query.districts : [NATIONWIDE_KEY];
    return { kind: 'region', params };
};

/** 같은 목록인지 가르는 문자열 — 목록 복원·재조회 판단용(보이지 않는 탭의 지역·역은 넣지 않는다) */
export const recruitmentQueryKey = (query: RecruitmentListQuery): string =>
    JSON.stringify([
        query.tab,
        query.tab === 'region' ? query.districts : query.stations,
        query.jobTasks,
        query.recruitmentTypes,
        query.careers,
        query.workdays,
        query.periods,
        query.workDates,
        query.welfares,
        query.emergency,
        query.payTimings,
        query.sort,
        query.search,
    ]);

/** [필터 N] 의 N — 지역·역·검색어는 세지 않는다 */
export const countRecruitmentConditions = (query: RecruitmentListQuery): number =>
    query.jobTasks.length + query.recruitmentTypes.length + query.careers.length + query.workdays.length
    + query.periods.length + (query.workDates.length > 0 ? 1 : 0) + query.welfares.length + (query.emergency ? 1 : 0) + query.payTimings.length;

/** 지역·역을 뺀 조건 또는 검색어가 걸려 있는지 */
export const hasRecruitmentCondition = (query: RecruitmentListQuery): boolean =>
    countRecruitmentConditions(query) > 0 || Boolean(query.search)
    || (query.tab === 'region' ? query.districts.length > 0 : query.stations.length > 0);

/** 조건(탭은 두고)을 모두 푼다 — 지역은 전국으로 */
export const clearRecruitmentConditions = (query: RecruitmentListQuery): RecruitmentListQuery => ({
    ...query,
    districts: query.tab === 'region' ? [] : query.districts,
    stations: query.tab === 'subway' ? [] : query.stations,
    jobTasks: [],
    recruitmentTypes: [],
    careers: [],
    workdays: [],
    periods: [],
    workDates: [],
    welfares: [],
    emergency: false,
    payTimings: [],
    search: '',
});

/** 고른 값을 선택지 순서대로 켜고 끈다 */
export const toggleInOrder = (list: string[], value: string, order: string[]): string[] =>
    list.includes(value)
        ? list.filter((item: string) => item !== value)
        : order.filter((item: string) => item === value || list.includes(item));

export const namesOf = (values: string[], options: RecruitmentOption[]): string[] =>
    values
        .map((value: string) => options.find((option: RecruitmentOption) => option.value === value)?.name)
        .filter((name: string | undefined): name is string => Boolean(name));

// ───────── 조건 저장 (common/savedListFilter) ─────────

/**
 * 저장하는 조건 — 탭, 고른 지역과 고른 역(보이지 않는 탭에서 고른 것도 함께, 2026-10-02 사용자 결정), 나머지 조건.
 * 정렬·검색어는 저장하지 않는다.
 */
const toSavableRecruitmentQuery = (query: RecruitmentListQuery): RecruitmentListQuery => ({
    ...query,
    sort: RECRUITMENT_SORT_DEFAULT,
    search: '',
});

/** 저장용 주소 쿼리 — 저장한 조건과 지금 조건이 같은지도 이 문자열로 견준다 */
export const savedRecruitmentQueryString = (query: RecruitmentListQuery): string =>
    writeRecruitmentListQuery(new URLSearchParams(), toSavableRecruitmentQuery(query)).toString();

/**
 * 저장한 조건과 같은 조건인지 견주는 문자열 — **탭은 빼고** 본다.
 * 탭은 "다음에 어느 탭으로 열지"일 뿐 조건이 아니다(지역·역을 둘 다 저장하므로 탭만 바꿔도 조건은 같다).
 * 탭까지 견주면 저장해 둔 채 탭만 바꿨을 때 [저장하기]로 보여 저장이 안 된 것처럼 보인다(2026-10-02 사용자 화면에서 확인).
 */
export const savedRecruitmentCompareKey = (query: RecruitmentListQuery): string =>
    savedRecruitmentQueryString({ ...query, tab: 'region' });

/** 저장한 주소 쿼리 → 조건. 지난 근무 날짜는 뺀다([today] = yyyy-MM-dd) */
export const parseSavedRecruitmentQuery = (savedQuery: string, today: string): RecruitmentListQuery => {
    const query: RecruitmentListQuery = parseRecruitmentListQuery(new URLSearchParams(savedQuery));
    return { ...query, workDates: query.workDates.filter((date: string) => date >= today) };
};

/**
 * 저장할 목록 API 인자(정렬 제외) — 서버가 새 공고를 이 조건에 맞춰 본다.
 * 위치는 탭과 상관없이 **고른 지역(`regionIds`)과 고른 역(`stationIds`·`areaIds`)을 둘 다** 넣는다 —
 * 알림은 고른 지역이거나 고른 역 근처면 간다(둘 다 안 골랐으면 전국). 목록 화면은 한 번에 한 탭만 보여 주므로 목록용 인자([toRecruitmentApiRequest])와 다르다.
 */
export const savedRecruitmentParams = (query: RecruitmentListQuery): Record<string, string[]> => {
    const { params } = toRecruitmentApiRequest({ ...toSavableRecruitmentQuery(query), tab: 'region', districts: [] });
    const result: Record<string, string[]> = Object.fromEntries(
        Object.entries(params)
            .filter(([key]: [string, string | string[]]) => key !== 'sort' && key !== 'regionIds')
            .map(([key, value]: [string, string | string[]]): [string, string[]] => [key, Array.isArray(value) ? value : [value]]),
    );
    if (query.districts.length > 0) result.regionIds = query.districts;
    const areaIds: string[] = query.stations.filter((id: string) => id.startsWith(AREA_ALL_PREFIX)).map((id: string) => id.slice(AREA_ALL_PREFIX.length));
    const stationIds: string[] = query.stations.filter((id: string) => !id.startsWith(AREA_ALL_PREFIX));
    if (stationIds.length > 0) result.stationIds = stationIds;
    if (areaIds.length > 0) result.areaIds = areaIds;
    return result;
};

/**
 * 조건 없이 들어온 주소인지 — 이때만 저장한 조건을 켠다(주소에 조건이 있으면 주소가 먼저).
 * 메뉴·홈 바로가기 주소(`category=REGION&districts=0_0`)처럼 탭·전국·정렬만 있는 주소도 조건 없는 주소다.
 * 직무 바로가기(`tab=job`)는 직무 창을 바로 여는 주소라 뺀다.
 */
export const isPlainRecruitmentEntry = (params: URLSearchParams): boolean =>
    params.get('tab') !== 'job' && !hasRecruitmentCondition(parseRecruitmentListQuery(params));

/**
 * 들어온 주소에 저장한 조건을 얹는다 — 보통은 저장할 때 보던 탭으로 연다.
 * 지하철역별 바로가기로 들어왔으면 그 탭을 그대로 두고(탭을 골라 들어온 것이 먼저) 저장한 역·조건을 켠다.
 */
export const savedRecruitmentForEntry = (entry: RecruitmentListQuery, saved: RecruitmentListQuery): RecruitmentListQuery => ({
    ...saved,
    tab: entry.tab === 'subway' ? 'subway' : saved.tab,
    sort: entry.sort,
});
